import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, EmptyState, Input, ProviderCard, Select, Skeleton } from "@/ds";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import AiSearch from "@/components/search/AiSearch";
import { useSmartCTA } from "@/hooks/useSmartCTA";
import api, { mediaUrl } from "@/lib/api";
import { categoryLabel } from "@/lib/catalog";

/**
 * The FixyBee directory, on Fable (DY1.4).
 *
 * This is where the AI-assisted search landed when the home's hero became
 * Dany's: describing a problem in your own words and getting ranked FixyBees
 * back is what this page is for, and keeping a second copy of it on the home
 * under Dany's composer was the duplication Jorge's spec removed.
 *
 * Two ways in, deliberately: describe the problem (`AiSearch`, ranked by
 * distance from a ZIP) or filter the whole directory by name and category. The
 * grid is a single `auto-fit` track — a fixed column count crushes cards once
 * Spanish copy wraps.
 */
export default function ProvidersDirectory() {
  const cta = useSmartCTA();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState("");
  // Initialize the category filter from the URL (?category=cat_x) so the Landing
  // category cards land here already filtered.
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (category) params.set("category", category);
    try {
      const { data } = await api.get(`/users/providers?${params}`);
      setProviders(data.providers || []);
    } finally {
      setLoading(false);
    }
  }, [q, category]);

  useEffect(() => {
    api.get("/catalog/categories").then((r) => setCategories(r.data.categories || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
    gap: "var(--card-gap)",
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "var(--bg-page)" }} data-testid="providers-directory">
      <Navbar />
      <main style={{ flex: 1, padding: "var(--section-padding-y) var(--section-padding-x)" }}>
        <div style={{ maxWidth: "var(--container-max)", margin: "0 auto" }}>
          <h1 style={{
            fontFamily: "var(--font-display)", fontWeight: 700,
            fontSize: "var(--text-section)", lineHeight: 1.15,
            letterSpacing: "var(--tracking-tight)",
          }}>{t("providers.title")}</h1>
          <p style={{
            marginTop: 8, fontSize: "var(--text-base)", lineHeight: "var(--leading-relaxed)",
            color: "var(--text-muted)", maxWidth: "var(--measure-lede)",
          }}>{t("providers.subtitle")}</p>

          {/* Describe the problem — G1's intent -> category -> ranked flow. */}
          <div style={{ marginTop: "var(--space-8)" }}>
            <AiSearch />
          </div>

          {/* Or filter the directory directly. */}
          <form
            onSubmit={(e) => { e.preventDefault(); load(); }}
            style={{
              marginTop: "var(--space-10)",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
              gap: "var(--space-3)",
            }}
          >
            <Input
              icon="search"
              placeholder={t("providers.search")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="dir-search"
            />
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="dir-category"
              options={[
                { value: "", label: t("providers.filterAll") },
                ...categories.map((c) => ({ value: c.category_id, label: categoryLabel(t, c) })),
              ]}
            />
          </form>

          <div style={{ marginTop: "var(--space-8)" }}>
            {loading ? (
              <div style={grid}>
                {Array(6).fill(0).map((_, i) => (
                  <Card key={i} padding="md"><Skeleton height={140} /></Card>
                ))}
              </div>
            ) : providers.length === 0 ? (
              /*
                "No FixyBees match these filters yet. Be the first — become a
                FixyBee" is a recruitment line, and it was shown to FixyBees.
                For a signed-in provider the filters are the story, not the
                signup; for a client the useful next move is to post the job and
                let FixyMatch find the trade the directory could not.
              */
              cta.isProvider ? (
                <EmptyState
                  title={t("providers.noResultsProvider")}
                  primaryAction={{ label: t("providers.backToDashboard"), onClick: () => navigate("/provider/dashboard") }}
                />
              ) : cta.isLoggedIn ? (
                <EmptyState
                  title={t("providers.noResultsAuth")}
                  primaryAction={{ label: t("providers.postInstead"), onClick: () => navigate(cta.postJob()) }}
                />
              ) : (
                <EmptyState
                  title={t("providers.noResults")}
                  primaryAction={{ label: t("providers.becomeOne"), onClick: () => navigate(cta.becomeFixyBee()) }}
                />
              )
            ) : (
              <div style={grid} data-testid="providers-grid">
                {providers.map((p) => (
                  <ProviderCard
                    key={p.user_id}
                    company={p.company_name || p.full_name}
                    name={p.full_name}
                    city={p.location_city}
                    state={p.location_state}
                    avatarUrl={p.avatar_url ? mediaUrl(p.avatar_url) : undefined}
                    verified={!!p.is_verified}
                    rating={p.average_rating || 0}
                    jobsCount={p.completed_jobs_count || 0}
                    bio={p.bio}
                    ctaLabel={t("providers.visit")}
                    verifiedLabel={t("providers.verified")}
                    newLabel={t("providers.newHere")}
                    jobsLabel={t("providers.jobsDone", { count: p.completed_jobs_count || 0 })}
                    onVisit={p.public_slug ? () => navigate(`/pro/${p.public_slug}`) : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
